// @flow
import BaseFilterModel from "../filters/BaseFilterModel";

import type { IFilter, AttributeType, ModelOptions } from "../types";
import { IllegalStateException } from "../../exceptions";
import createAttribute from "../attributes/_createAttribute";

/**
 * Assignment filter consists of two filters: assignment type and user filter
 */
export default class AssignmentFilterModel
  extends BaseFilterModel
  implements IFilter
{
  _assignmenttype: AttributeType;
  _user: AttributeType;

  /**
   * Construct an assignment filter
   */
  constructor(
    data: Object,
    contributions: Object,
    modelOptions?: ModelOptions,
  ) {
    super(data, contributions, modelOptions);

    this._assignmenttype = this.createAssignmentTypeModel();
    this._user = this.createUserModel();
  }

  /**
   * Creates an assignmenttype model when assignmenttype json is present
   */
  createAssignmentTypeModel(): AttributeType {
    const key = `${this.contextid}ASSIGNMENTTYPE`;
    const assignmentTypeData = this.data[key];

    if (typeof assignmentTypeData === "undefined") {
      throw new IllegalStateException(
        `Missing assignment type data for ${key}`,
      );
    }

    if (this.data.dynamicschema && this.data.dynamicschema[key]) {
      assignmentTypeData.dynamicschema = {
        [key]: this.data.dynamicschema[key],
      };
    }

    const assignmentTypeContributions = {
      ...this.contributions[key],
      type: "choice",
      enumerated: true,
      optionMode: assignmentTypeData._links ? "lookup" : "static",
    };

    return createAttribute(
      assignmentTypeData.name || assignmentTypeData.param,
      assignmentTypeData,
      assignmentTypeContributions,
      this.modelOptions,
    );
  }

  /**
   * Creates an assignmenttype model when userkey json is present
   */
  createUserModel(): AttributeType {
    const key = `${this.contextid}USERKEY`;
    const userData = this.data[key];

    if (this.data.dynamicschema && this.data.dynamicschema[key]) {
      userData.dynamicschema = {
        [key]: this.data.dynamicschema[key],
      };
    }

    const userContributions = {
      ...this.contributions[key],
      type: "choice",
      enumerated: true,
      optionMode: userData._links ? "lookup" : "static",
    };

    return createAttribute(
      userData.name || userData.param,
      userData,
      userContributions,
      this.modelOptions,
    );
  }

  /**
   * The assignment filter consists of two part. This method return the assignment type attribute
   */
  get assignmenttype(): AttributeType {
    return this._assignmenttype;
  }

  /**
   * The assignment filter consists of two part. This method return the user identifier attribute
   */
  get user(): AttributeType {
    return this._user;
  }

  /**
   * Getting the parameters of this filter
   */
  get params(): Array<{ name: string, value: ?string }> {
    return [
      {
        name: this.assignmenttype.name,
        value: this.assignmenttype.value,
      },
      {
        name: this.user.name,
        value: this.user.value,
      },
    ];
  }

  /**
   */
  get value(): { [string]: any } {
    return {
      [this.assignmenttype.name]: this.assignmenttype.value,
      [this.user.name]: this.user.value,
    };
  }

  /**
   */
  get inputvalue(): { [string]: any } {
    return {
      [this.assignmenttype.name]: this.assignmenttype.inputvalue,
      [this.user.name]: this.user.inputvalue,
    };
  }

  /**
   * Indicates if the filter has a value.
   */
  hasValue(): boolean {
    return (
      (this.assignmenttype.value !== null &&
        this.assignmenttype.value !== "") ||
      (this.user.value !== null && this.user.value !== "")
    );
  }

  /**
   * Reset the values within the filter
   */
  reset(): AssignmentFilterModel {
    if (this.assignmenttype) {
      this.assignmenttype.reset();
    }
    if (this.user) {
      this.user.reset();
    }

    return this;
  }

  /**
   * Update this filter
   */
  update(attribute: AttributeType, value: string) {
    if (this.user != null && this.user.key === attribute.key) {
      this.user.update(value);
    } else if (
      this.assignmenttype != null &&
      this.assignmenttype.key === attribute.key
    ) {
      this.assignmenttype.update(value);
    }
  }

  /**
   * Inidiates if filter is active
   */
  isActive(): boolean {
    return (
      (this.assignmenttype != null && this.assignmenttype.initvalue !== null) ||
      (this.user != null && this.user.initvalue !== null)
    );
  }

  /**
   * Inidiates if filter is valid
   */
  get isValid(): boolean {
    return (
      this.assignmenttype != null &&
      this.assignmenttype.isValid &&
      this.user != null &&
      this.user.isValid
    );
  }

  /**
   */
  get readonlyvalue(): { [string]: string } {
    return {
      [this.assignmenttype.name]: this.assignmenttype.readonlyvalue,
      [this.user.name]: this.user.readonlyvalue,
    };
  }

  /**
   */
  get formdata(): { [string]: any } | null {
    return this.value;
  }
}
